/**
 * Turns a (still growing) chat answer into phrases that can be spoken as soon
 * as they are complete, so the assistant starts talking while it is still writing.
 */

const MAX_CHUNK = 500;
const MIN_CHUNK = 28;
/** The first phrase of a reply is kept short so the voice starts as early as possible. */
const EAGER_MIN_CHUNK = 12;
/** …and may end at a clause break once it is this long, instead of waiting for a full stop. */
const EAGER_CLAUSE_MIN = 32;

/** Markdown and decorations a voice must not read out. */
export function toSpeakableText(raw: string): string {
  return raw
    .replace(/```[\s\S]*?(```|$)/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+[.)]\s+/gm, '')
    .replace(/[*_~]{1,3}([^*_~\n]+)[*_~]{1,3}/g, '$1')
    .replace(/[>|]/g, ' ')
    .replace(/\p{Extended_Pictographic}/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface SpeakableStep {
  chunks: string[];
  /** Pass back as `consumed` next time. */
  consumed: number;
}

/**
 * @param text the answer so far (grows between calls)
 * @param consumed how much of `text` was already handed out
 * @param final true once the answer is complete: flush whatever is left
 */
export function nextSpeakable(
  text: string,
  consumed: number,
  final: boolean,
  options: { eager?: boolean } = {},
): SpeakableStep {
  // Eagerness is only for the first phrase of a reply; once one is out, the usual rules apply again.
  let eager = options.eager === true;
  // Never read into an unfinished code block.
  const openFence = unclosedFenceStart(text, consumed);
  const limit = openFence === -1 || final ? text.length : openFence;
  const region = text.slice(consumed, limit);

  const chunks: string[] = [];
  let used = 0;
  let pending = '';
  let pendingEnd = 0;

  // Eager mode also accepts a comma/semicolon/dash break, so a long opening sentence starts sooner.
  const boundary = options.eager
    ? /[.!?…]+["'»)\]]*(?=\s|$)|[,;:—–-](?=\s)|\n+/g
    : /[.!?…]+["'»)\]]*(?=\s|$)|\n+/g;
  for (let match = boundary.exec(region); match; match = boundary.exec(region)) {
    const end = match.index + match[0].length;
    // A terminator at the very end of a still-growing text may be "3." of "3.5": wait for more.
    if (!final && end === region.length && !/\n$/.test(match[0])) break;
    pending += region.slice(pendingEnd, end);
    pendingEnd = end;
    const clauseBreak = /[,;:—–-]$/.test(match[0]);
    if (clauseBreak && !eager) continue; // a comma is only a boundary for the opening phrase
    const spoken = toSpeakableText(pending);
    if (spoken.length >= (clauseBreak ? EAGER_CLAUSE_MIN : eager ? EAGER_MIN_CHUNK : MIN_CHUNK)) {
      chunks.push(...splitLong(spoken));
      pending = '';
      used = end;
      eager = false;
    }
  }

  if (final) {
    const rest = toSpeakableText(pending + region.slice(pendingEnd));
    if (rest) chunks.push(...splitLong(rest));
    return { chunks, consumed: text.length };
  }
  return { chunks, consumed: consumed + used };
}

function unclosedFenceStart(text: string, from: number): number {
  const fence = /```/g;
  fence.lastIndex = 0;
  let open = -1;
  for (let m = fence.exec(text); m; m = fence.exec(text)) {
    open = open === -1 ? m.index : -1;
  }
  return open >= from ? open : -1;
}

function splitLong(text: string): string[] {
  if (text.length <= MAX_CHUNK) return [text];
  const parts: string[] = [];
  let rest = text;
  while (rest.length > MAX_CHUNK) {
    const window = rest.slice(0, MAX_CHUNK);
    const cut = Math.max(window.lastIndexOf(', '), window.lastIndexOf('; '), window.lastIndexOf(' '));
    const at = cut > MAX_CHUNK / 2 ? cut + 1 : MAX_CHUNK;
    parts.push(rest.slice(0, at).trim());
    rest = rest.slice(at).trim();
  }
  if (rest) parts.push(rest);
  return parts;
}
